import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/use-auth';
import { GoogleSignInButton } from '@/modules/auth/GoogleSignInButton';
import { getErrorMessage } from '@/lib/errors';
import { destinationAfterLogin } from '@/lib/redirect';
import { usePageMeta } from '@/lib/seo';
import { PAGES } from '@/seo/pages';

/**
 * One form, two doors.
 *
 * `/login` is the customer entrance and `/admin/login` the staff one. Both post the same
 * credentials to the same endpoint — the role comes from the account, never from the URL — so this
 * is a matter of not advertising the admin panel and of showing staff a screen that does not invite
 * them to create a customer account. It is deliberately **not** a security boundary, and should not
 * be described as one.
 */
export function LoginPage({ staff = false }: { staff?: boolean } = {}) {
  // The staff door is `noindex` in its own entry; the customer one is indexed on purpose,
  // because "sign in to <product>" is one of the most common searches a customer makes.
  usePageMeta(staff ? PAGES.staffLogin : PAGES.login);

  const { login, completeTwoFactor } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Where they were headed before being asked to sign in. Null means they came here directly,
  // in which case the destination depends on the role — which is not known until the credentials
  // come back, so it cannot be resolved at render time the way this value can.
  const from = (location.state as { from?: string } | null)?.from ?? null;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmitCredentials(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.two_factor_required) {
        setChallengeToken(result.challenge_token);
      } else {
        navigate(destinationAfterLogin(from, result.user), { replace: true });
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function onSubmitCode(e: React.FormEvent) {
    e.preventDefault();
    if (!challengeToken) return;
    setError(null);
    setLoading(true);
    try {
      const result = await completeTwoFactor(challengeToken, code);
      navigate(destinationAfterLogin(from, result.user), { replace: true });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      title={challengeToken ? 'Two-factor authentication' : staff ? 'Staff sign in' : 'Welcome back'}
      subtitle={
        challengeToken
          ? 'Enter the 6-digit code from your authenticator app.'
          : staff
            ? 'Administrator and support access.'
            : 'Sign in to your account'
      }
      footer={
        !challengeToken &&
        !staff && (
          <>
            Don't have an account?{' '}
            <Link to="/register" className="font-medium text-primary hover:underline">
              Create one
            </Link>
          </>
        )
      }
    >
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {challengeToken ? (
        <form onSubmit={onSubmitCode} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="code">Authentication code</Label>
            <Input
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={6}
              required
              autoFocus
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading && <Loader2 className="animate-spin" />} Verify
          </Button>
        </form>
      ) : (
        <form onSubmit={onSubmitCredentials} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link to="/forgot-password" className="text-xs text-primary hover:underline">
                Forgot password?
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading && <Loader2 className="animate-spin" />} Sign in
          </Button>

          {/* Renders nothing when this installation has no Google credentials. */}
          <GoogleSignInButton label="Continue with Google" />
        </form>
      )}
    </AuthLayout>
  );
}
